import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import NewsCard from '@/components/base/NewsCard';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { newsCategories, newsList } from '@/mocks/news';

const PAGE_SIZE = 6;

export default function NewsPage() {
  const [category, setCategory] = useState('全部资讯');
  const [page, setPage] = useState(1);

  useDocumentMeta({
    title: '影视资讯专题 - 最新影视动态与行业观察 - 粉色水蜜桃',
    description:
      '粉色水蜜桃资讯专题聚合最新电影新闻、电视剧动态与行业观察，为你带来及时的影视资讯与深度解读。',
    keywords: '粉色水蜜桃,影视资讯,电影新闻,电视剧动态,行业资讯,影视动态',
  });

  const filtered = useMemo(
    () =>
      category === '全部资讯'
        ? newsList
        : newsList.filter((item) => item.category === category),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const [first, ...rest] = paged;

  const changeCategory = (next: string) => {
    setCategory(next);
    setPage(1);
  };

  const goPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-800">资讯专题</span>
      </nav>

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          影视资讯专题
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          电影新闻、电视剧动态与行业观察，按发布时间倒序更新（演示数据）
        </p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {newsCategories.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => changeCategory(item)}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              category === item
                ? 'border-primary-400 bg-primary-50 text-primary-700'
                : 'border-background-300 text-foreground-700 hover:border-primary-300'
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      {paged.length > 0 ? (
        <>
          {first ? (
            <Link
              to={`/news/${first.id}`}
              className="group mt-8 grid gap-6 overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:grid-cols-2"
            >
              <div className="aspect-[16/9] w-full overflow-hidden bg-background-200 lg:aspect-auto lg:h-full">
                <img
                  src={first.cover}
                  alt={`${first.title}配图`}
                  title={`${first.title} 粉色水蜜桃资讯`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col justify-center p-5 md:p-8">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-800">
                    头条
                  </span>
                  <span className="rounded-full bg-primary-50 px-2.5 py-0.5 font-medium text-primary-600">
                    {first.category}
                  </span>
                  <span>{first.date}</span>
                </div>
                <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                  {first.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{first.summary}</p>
                <span className="mt-4 flex items-center gap-1 text-sm font-medium text-primary-600">
                  阅读全文
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </div>
            </Link>
          ) : null}

          {rest.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rest.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 py-20 text-center">
          <i className="ri-newspaper-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <p className="mt-3 text-base font-medium text-foreground-800">暂无相关资讯</p>
          <p className="mt-1 text-sm text-foreground-500">换一个分类看看吧</p>
        </div>
      )}

      {totalPages > 1 ? (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => goPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="flex h-9 items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
            上一页
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goPage(i + 1)}
              className={`h-9 w-9 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                currentPage === i + 1
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'border border-background-300 text-foreground-700 hover:border-primary-300'
              }`}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            onClick={() => goPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="flex h-9 items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            下一页
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </div>
      ) : null}
    </div>
  );
}